export interface GlossarTerm {
  slug: string;
  name: string;
  category: "bedrohungen" | "compliance" | "sicherheit" | "medizin" | "incident";
  shortDef: string;
  longDescription: string;
  howItWorks: string;
  exampleMedical: string;
  prevention: string[];
  insuranceCoverage: string;
  relatedTerms: string[];
  resources: { title: string; url: string }[];
}

export const glossarTerms: GlossarTerm[] = [
  {
    slug: "ransomware",
    name: "Ransomware",
    category: "bedrohungen",
    shortDef: "Ransomware ist Schadsoftware, die Dateien verschlüsselt und Lösegeld für die Freigabe fordert. Arztpraxen sind bevorzugte Ziele, da Patientendaten lebensnotwendig sind.",
    longDescription: "Ransomware ist eine der gefährlichsten Cyber-Bedrohungen für Arztpraxen. Die Schadsoftware infiltriert Systeme über Phishing-E-Mails, unsichere Downloads oder Sicherheitslücken und verschlüsselt alle zugänglichen Dateien. Anschließend erscheint eine Lösegeldforderung, oft in Bitcoin, mit einer Frist zur Zahlung. Ohne Backup sind Patientendaten unwiederbringlich verloren.",
    howItWorks: "Ransomware nutzt asymmetrische Verschlüsselung: Der Angreifer generiert ein Schlüsselpaar, verschlüsselt Ihre Daten mit dem öffentlichen Schlüssel und behält den privaten Schlüssel. Ohne diesen Schlüssel ist eine Entschlüsselung praktisch unmöglich.",
    exampleMedical: "Eine Hausarztpraxis erhält eine E-Mail vermeintlich vom Labor. Beim Öffnen aktiviert sich Ransomware, die innerhalb von Minuten das gesamte Netzwerk verschlüsselt.",
    prevention: [
      "Tägliche, verschlüsselte Offline-Backups (3-2-1-Regel)",
      "E-Mail-Filter gegen Phishing",
      "Endpoint-Detection-Response (EDR) Software",
      "Regelmäßige Software-Updates",
      "Netzwerksegmentierung",
      "Mitarbeiter-Schulungen"
    ],
    insuranceCoverage: "Cyberversicherungen decken Lösegeld, Datenwiederherstellung, IT-Forensik, Betriebsunterbrechung und Rechtsberatung ab.",
    relatedTerms: ["phishing", "backup", "verschluesselung", "dsgvo"],
    resources: []
  },
  {
    slug: "phishing",
    name: "Phishing",
    category: "bedrohungen",
    shortDef: "Betrügerische E-Mails oder Websites, die Nutzer zur Preisgabe sensibler Daten verleiten.",
    longDescription: "Phishing ist die häufigste Angriffsmethode auf Arztpraxen. Angreifer versenden täuschend echte E-Mails, die von Laboren, Krankenkassen oder IT-Dienstleistern zu stammen scheinen.",
    howItWorks: "Phishing nutzt Social Engineering: Angreifer imitieren vertrauenswürdige Absender und erzeugen Dringlichkeit. Die gefälschten E-Mails enthalten Links zu Fake-Websites.",
    exampleMedical: "Ein Praxis-Mitarbeiter erhält eine E-Mail von 'KBV' mit der Aufforderung, dringend Abrechnungsdaten zu aktualisieren. Nach Login-Eingabe erhalten Angreifer Zugang zum Praxisverwaltungssystem.",
    prevention: [
      "E-Mail-Authentifizierung implementieren",
      "Phishing-Filter in E-Mail-Server",
      "Zwei-Faktor-Authentifizierung",
      "Mitarbeiter-Schulungen",
      "Misstrauisch bei Links in E-Mails"
    ],
    insuranceCoverage: "Cyberversicherungen decken Schäden durch Phishing ab: Datenwiederherstellung, forensische Analyse, DSGVO-Bußgelder.",
    relatedTerms: ["social-engineering", "2fa", "malware", "dsgvo"],
    resources: []
  },
  {
    slug: "malware",
    name: "Malware",
    category: "bedrohungen",
    shortDef: "Schadsoftware (Viren, Trojaner, Würmer), die Systeme infiziert, Daten stiehlt oder Schäden verursacht.",
    longDescription: "Malware ist ein Überbegriff für jede Art von bösartiger Software. Dazu gehören Viren, Würmer, Trojaner, Spyware und Ransomware. Malware kann Praxis-Systeme lahmlegen oder als Einfallstor für weitere Angriffe dienen.",
    howItWorks: "Malware gelangt über infizierte E-Mail-Anhänge, Downloads, USB-Sticks oder Software-Schwachstellen ins System. Nach Installation führt sie verschiedene Aktionen aus.",
    exampleMedical: "Ein Zahnarzt lädt eine vermeintliche Software zur Abrechnung herunter. Die Datei enthält einen Trojaner, der sich im Netzwerk verbreitet und Patientendaten exfiltriert.",
    prevention: [
      "Aktuelle Antivirus-Software",
      "EDR-Lösungen einsetzen",
      "Regelmäßige Software-Updates",
      "Application Whitelisting",
      "USB-Ports kontrollieren"
    ],
    insuranceCoverage: "Kosten für Malware-Entfernung, Systemwiederherstellung, Datenforensik und DSGVO-Bußgelder werden übernommen.",
    relatedTerms: ["ransomware", "spyware", "edr"],
    resources: []
  },
  {
    slug: "social-engineering",
    name: "Social Engineering",
    category: "bedrohungen",
    shortDef: "Psychologische Manipulation, um Personen zur Preisgabe vertraulicher Informationen zu bewegen.",
    longDescription: "Social Engineering ist keine technische, sondern eine psychologische Angriffsmethode. Kriminelle nutzen menschliche Eigenschaften wie Hilfsbereitschaft oder Zeitdruck zur Manipulation.",
    howItWorks: "Angreifer recherchieren Informationen über Zielpersonen und erstellen glaubwürdige Szenarien. Sie geben sich als Kollegen, IT-Support oder Behördenvertreter aus.",
    exampleMedical: "Ein Anrufer gibt sich als IT-Dienstleister aus und behauptet, dringend ein Sicherheits-Update durchführen zu müssen. Die Mitarbeiterin gibt ihr Passwort preis.",
    prevention: [
      "Mitarbeiter-Schulungen",
      "Niemals Passwörter am Telefon preisgeben",
      "Vier-Augen-Prinzip bei Überweisungen",
      "Rückruf-Verfahren bei verdächtigen Anfragen"
    ],
    insuranceCoverage: "Social-Engineering-Schäden sind teilweise versichert, technische Folgeschäden meist vollständig.",
    relatedTerms: ["phishing", "2fa"],
    resources: []
  },
  {
    slug: "ddos",
    name: "DDoS-Angriff",
    category: "bedrohungen",
    shortDef: "Überlastungsangriffe, die Websites oder Server durch massive Anfragen lahmlegen.",
    longDescription: "DDoS-Angriffe zielen darauf ab, Dienste durch Überlastung lahmzulegen. Angreifer nutzen Botnets, um gleichzeitig Millionen Anfragen zu senden.",
    howItWorks: "Botnets aus IoT-Geräten senden legitim erscheinende Anfragen. Da Anfragen von Millionen IP-Adressen kommen, ist Blockierung schwierig.",
    exampleMedical: "Eine MVZ-Klinik mit Online-Terminbuchung wird Ziel eines DDoS-Angriffs. 2 Tage lang ist die Website unerreichbar.",
    prevention: [
      "DDoS-Schutz-Services nutzen",
      "Content Delivery Network einsetzen",
      "Rate-Limiting konfigurieren",
      "Traffic-Monitoring"
    ],
    insuranceCoverage: "Cyberversicherungen decken Betriebsunterbrechung und DDoS-Mitigation-Kosten ab.",
    relatedTerms: ["firewall"],
    resources: []
  },
  {
    slug: "zero-day",
    name: "Zero-Day-Exploit",
    category: "bedrohungen",
    shortDef: "Angriff auf eine bisher unbekannte Sicherheitslücke, für die noch kein Patch existiert.",
    longDescription: "Ein Zero-Day-Exploit nutzt Sicherheitslücken in Software aus, die dem Hersteller noch nicht bekannt sind. Da es noch keine Patches gibt, sind betroffene Systeme besonders verwundbar.",
    howItWorks: "Angreifer entdecken Sicherheitslücken in Software und nutzen diese aus, bevor der Hersteller davon erfährt oder ein Update bereitstellen kann.",
    exampleMedical: "Praxissoftware hat unbekannte Schwachstelle, über die Angreifer Patientendaten auslesen können.",
    prevention: [
      "Regelmäßige Sicherheitsupdates",
      "Mehrschichtige Sicherheitsarchitektur",
      "Intrusion Detection Systems",
      "Application Whitelisting"
    ],
    insuranceCoverage: "Schäden durch Zero-Day-Exploits sind oft mitversichert, wenn dadurch Datenverlust oder Betriebsunterbrechung entsteht.",
    relatedTerms: ["malware", "patch-management"],
    resources: []
  },
  {
    slug: "botnet",
    name: "Botnet",
    category: "bedrohungen",
    shortDef: "Netzwerk aus kompromittierten Geräten, die ferngesteuert für Angriffe missbraucht werden.",
    longDescription: "Ein Botnet besteht aus vielen infizierten Computern, die zentral gesteuert werden können. Sie werden für DDoS-Angriffe, Spam-Versand oder Krypto-Mining missbraucht.",
    howItWorks: "Malware infiziert Systeme und verbindet sie mit einem Command-and-Control-Server. Der Angreifer kann dann alle Bots gleichzeitig steuern.",
    exampleMedical: "Praxis-PCs werden Teil eines Botnets und für DDoS-Angriffe auf Dritte missbraucht.",
    prevention: [
      "Endpoint-Protection einsetzen",
      "Netzwerk-Monitoring",
      "Ausgehenden Traffic überwachen",
      "IoT-Geräte absichern"
    ],
    insuranceCoverage: "Haftpflichtschäden und Abwehrkosten bei Botnet-Beteiligung sind oft mitversichert.",
    relatedTerms: ["ddos", "malware"],
    resources: []
  },
  {
    slug: "spyware",
    name: "Spyware",
    category: "bedrohungen",
    shortDef: "Schadsoftware, die heimlich Daten ausspäht und an Dritte sendet.",
    longDescription: "Spyware sammelt unbemerkt Informationen über Nutzer und deren Aktivitäten. In medizinischen Einrichtungen können dadurch sensible Patientendaten gefährdet werden.",
    howItWorks: "Nach der Installation läuft Spyware im Hintergrund und protokolliert Tastatureingaben, Screenshots oder Browsing-Verhalten.",
    exampleMedical: "Keylogger zeichnet Passwörter und Patientendaten auf, die dann an Cyberkriminelle übermittelt werden.",
    prevention: [
      "Anti-Spyware-Software nutzen",
      "Nur vertrauenswürdige Software installieren",
      "Regelmäßige System-Scans",
      "Benutzerrechte einschränken"
    ],
    insuranceCoverage: "Datenschutzverletzungen durch Spyware sind versichert, inklusive Meldepflichten und Schadensersatz.",
    relatedTerms: ["malware", "datenschutz"],
    resources: []
  },
  {
    slug: "dsgvo",
    name: "DSGVO",
    category: "compliance",
    shortDef: "EU-Datenschutzverordnung, die Verarbeitung personenbezogener Daten regelt. Verstöße führen zu Bußgeldern bis 20 Mio. €.",
    longDescription: "Die DSGVO gilt seit Mai 2018 und regelt die Verarbeitung personenbezogener Daten EU-weit. Für Arztpraxen besonders relevant sind Rechtsgrundlagen, Informationspflichten, Betroffenenrechte und Meldepflicht bei Datenpannen.",
    howItWorks: "DSGVO fordert 'Privacy by Design': Datenschutz muss von Anfang an in Systeme integriert sein. Arztpraxen müssen Einwilligungen dokumentieren, Daten minimieren und Verschlüsselung verwenden.",
    exampleMedical: "Eine Arztpraxis erleidet Ransomware-Angriff. 1.200 Patientendaten werden verschlüsselt. Praxis meldet Vorfall erst nach 10 Tagen statt 72h. Bußgeld: 85.000€.",
    prevention: [
      "Datenschutzbeauftragten bestellen",
      "Verarbeitungsverzeichnis führen",
      "Verschlüsselung implementieren",
      "Zugriffskontrolle einrichten",
      "Auftragsverarbeitungsverträge abschließen"
    ],
    insuranceCoverage: "Cyberversicherungen decken DSGVO-Bußgelder vollständig ab, auch bei Fahrlässigkeit.",
    relatedTerms: ["bdsg", "verschluesselung"],
    resources: []
  },
  {
    slug: "bdsg",
    name: "BDSG",
    category: "compliance",
    shortDef: "Bundesdatenschutzgesetz, das DSGVO-Regelungen national konkretisiert.",
    longDescription: "Das BDSG ergänzt die DSGVO um nationale Regelungen. Wichtig für Arztpraxen: Datenschutzbeauftragten-Pflicht ab 20 Mitarbeitern, Sanktionen bei Verstößen.",
    howItWorks: "BDSG gilt parallel zur DSGVO: Bestellung Datenschutzbeauftragter ist Pflicht ab 20 Mitarbeitern, die regelmäßig personenbezogene Daten verarbeiten.",
    exampleMedical: "Eine Gemeinschaftspraxis mit 22 Mitarbeitern bestellt keinen Datenschutzbeauftragten. Bei Routine-Prüfung wird Verstoß festgestellt. Bußgeld: 12.000€.",
    prevention: [
      "Datenschutzbeauftragten ab 20 Mitarbeitern bestellen",
      "Externe DSB-Dienstleister nutzen",
      "Mitarbeiter-Datenschutz regeln",
      "Schweigepflicht-Verpflichtung"
    ],
    insuranceCoverage: "BDSG-Bußgelder sind durch Cyberversicherungen gedeckt.",
    relatedTerms: ["dsgvo"],
    resources: []
  },
  {
    slug: "epa",
    name: "ePA (elektronische Patientenakte)",
    category: "medizin",
    shortDef: "Digitale Patientenakte mit Befunden, Diagnosen und Medikation. Zugriff über Telematikinfrastruktur.",
    longDescription: "Die ePA ermöglicht Patienten, ihre Gesundheitsdaten zentral zu speichern und Ärzten Zugriff zu gewähren. Seit 2021 haben alle gesetzlich Versicherten Anspruch auf ePA.",
    howItWorks: "ePA wird von Krankenkassen bereitgestellt, Zugriff erfolgt über Telematikinfrastruktur. Patienten können Zugriffe verwalten und Daten freigeben.",
    exampleMedical: "Patient gibt Hausarzt Zugriff auf ePA mit Vorbefunden vom Facharzt. Arzt kann Diagnosen und Medikation einsehen.",
    prevention: [
      "Sichere TI-Anbindung",
      "Zugriffskontrolle implementieren",
      "Schulung der Mitarbeiter",
      "Einwilligungen dokumentieren"
    ],
    insuranceCoverage: "Datenschutzverletzungen bei ePA-Zugriff sind versichert.",
    relatedTerms: ["pvs", "telematikinfrastruktur"],
    resources: []
  },
  {
    slug: "telematikinfrastruktur",
    name: "Telematikinfrastruktur (TI)",
    category: "medizin",
    shortDef: "Sichere Netzwerkinfrastruktur für digitale Gesundheitsdienste wie ePA, E-Rezept.",
    longDescription: "Die TI vernetzt alle Akteure im Gesundheitswesen sicher miteinander. Über Konnektoren werden Praxen angebunden.",
    howItWorks: "TI nutzt Ende-zu-Ende-Verschlüsselung und Authentifizierung über Heilberufsausweise. Konnektoren verbinden Praxissysteme mit TI.",
    exampleMedical: "Arztpraxis nutzt TI für E-Rezept-Ausstellung und ePA-Zugriff.",
    prevention: [
      "Konnektor aktuell halten",
      "Sichere Konfiguration",
      "Heilberufsausweise schützen",
      "Zugriffskontrollen implementieren"
    ],
    insuranceCoverage: "TI-bezogene Sicherheitsvorfälle sind versichert.",
    relatedTerms: ["epa", "e-rezept"],
    resources: []
  },
  {
    slug: "firewall",
    name: "Firewall",
    category: "sicherheit",
    shortDef: "Netzwerksicherheitssystem, das ein- und ausgehenden Datenverkehr überwacht und filtert.",
    longDescription: "Firewalls bilden eine Schutzbarriere zwischen vertrauenswürdigen internen und unsicheren externen Netzwerken. Sie blockieren unautorisierten Zugriff.",
    howItWorks: "Firewalls prüfen Datenpakete anhand von Regelwerken und blockieren verdächtigen Traffic. Moderne Firewalls bieten auch Intrusion Prevention.",
    exampleMedical: "Praxis-Firewall blockiert Angriffsversuche aus dem Internet auf Praxisserver.",
    prevention: [
      "Professionelle Firewall einsetzen",
      "Regelwerk regelmäßig aktualisieren",
      "Logs auswerten",
      "Netzwerksegmentierung nutzen"
    ],
    insuranceCoverage: "Firewall-Schutz wird von Versicherern oft vorausgesetzt.",
    relatedTerms: ["netzwerksegmentierung"],
    resources: []
  },
  {
    slug: "vpn",
    name: "VPN (Virtual Private Network)",
    category: "sicherheit",
    shortDef: "Verschlüsselte Verbindung über öffentliche Netzwerke für sicheren Remote-Zugriff.",
    longDescription: "VPNs ermöglichen sichere Verbindungen über unsichere Netzwerke wie das Internet. Für Arztpraxen wichtig für Heimarbeitsplätze und mobile Zugriffe.",
    howItWorks: "VPN verschlüsselt gesamten Datenverkehr zwischen Client und Server. Authentifizierung erfolgt über Zertifikate oder Passwörter.",
    exampleMedical: "Arzt greift von zu Hause über VPN sicher auf Praxissoftware zu.",
    prevention: [
      "Starke Verschlüsselung verwenden",
      "Multi-Faktor-Authentifizierung",
      "VPN-Software aktuell halten",
      "Zugriffsprotokolle führen"
    ],
    insuranceCoverage: "VPN-Nutzung ist oft Versicherungsvoraussetzung für Remote-Zugriffe.",
    relatedTerms: ["verschluesselung", "2fa"],
    resources: []
  },
  {
    slug: "2fa",
    name: "Zwei-Faktor-Authentifizierung (2FA)",
    category: "sicherheit",
    shortDef: "Anmeldung mit zwei unabhängigen Faktoren (z.B. Passwort + SMS-Code) für höhere Sicherheit.",
    longDescription: "2FA kombiniert zwei verschiedene Faktoren: Wissen (Passwort), Besitz (Token) oder biometrisches Merkmal (Fingerabdruck).",
    howItWorks: "Neben dem Passwort wird ein zweiter Faktor wie SMS-Code, Authenticator-App oder Hardware-Token benötigt.",
    exampleMedical: "Zugriff auf Praxissoftware erfordert Passwort UND Code aus Authenticator-App.",
    prevention: [
      "2FA für alle kritischen Systeme aktivieren",
      "Authenticator-Apps bevorzugen",
      "Backup-Codes sicher verwahren",
      "Hardware-Token für Admin-Zugriffe"
    ],
    insuranceCoverage: "2FA wird von vielen Versicherern mittlerweile vorausgesetzt.",
    relatedTerms: ["mfa", "identity-management"],
    resources: []
  },
  {
    slug: "verschluesselung",
    name: "Verschlüsselung",
    category: "sicherheit",
    shortDef: "Umwandlung von Daten in unlesbares Format zum Schutz vor unbefugtem Zugriff.",
    longDescription: "Verschlüsselung ist essentiell für den Schutz sensibler Patientendaten. Sie schützt Daten bei Übertragung und Speicherung.",
    howItWorks: "Verschlüsselungsalgorithmen verwandeln Klartext in Chiffretext. Nur mit dem richtigen Schlüssel können Daten wieder entschlüsselt werden.",
    exampleMedical: "Patientendaten werden verschlüsselt gespeichert. Selbst bei Diebstahl des Servers sind Daten unlesbar.",
    prevention: [
      "Ende-zu-Ende-Verschlüsselung nutzen",
      "Starke Algorithmen verwenden (AES-256)",
      "Verschlüsselte Backups",
      "Schlüsselverwaltung sicher gestalten"
    ],
    insuranceCoverage: "Verschlüsselung ist oft Versicherungsvoraussetzung. Fehlt sie, kann Versicherungsschutz entfallen.",
    relatedTerms: ["dsgvo", "backup"],
    resources: []
  },
  {
    slug: "backup",
    name: "Backup",
    category: "sicherheit",
    shortDef: "Sicherungskopien von Daten zur Wiederherstellung bei Verlust. Goldene Regel: 3-2-1-Backup.",
    longDescription: "Backups sind die wichtigste Schutzmaßnahme gegen Ransomware und Datenverlust. 3-2-1-Regel: 3 Kopien der Daten, 2 verschiedene Medien, 1 Backup extern/offline.",
    howItWorks: "Backup-Software kopiert täglich Daten auf Backup-Ziel. Offline-Backup wird nach Erstellung vom Netzwerk getrennt.",
    exampleMedical: "Arztpraxis erleidet Ransomware-Angriff. Mit Offline-Backup nach 6h wieder betriebsbereit.",
    prevention: [
      "3-2-1-Backup-Strategie umsetzen",
      "Verschlüsselte Backups",
      "Automatische tägliche Backups",
      "Monatliche Restore-Tests"
    ],
    insuranceCoverage: "Cyberversicherungen fordern oft Backups als Bedingung. Ohne Backup kann Leistung stark reduziert sein.",
    relatedTerms: ["ransomware", "verschluesselung"],
    resources: []
  },
  {
    slug: "kis",
    name: "KIS (Krankenhausinformationssystem)",
    category: "medizin",
    shortDef: "Zentrale Software in Krankenhäusern zur Verwaltung von Patientendaten, Abrechnung, Labor, Radiologie.",
    longDescription: "KIS integriert alle klinischen und administrativen Prozesse: Patientenaufnahme, Dokumentation, Befunde, Labor, Radiologie, OP-Planung, Medikation, Abrechnung.",
    howItWorks: "KIS läuft meist auf Windows-Servern. Clients greifen per Citrix/RDP auf KIS zu. Schnittstellen: HL7, FHIR, DICOM.",
    exampleMedical: "Universitätsklinik erleidet Ransomware-Angriff auf KIS. 10 Tage Totalausfall. 240 OPs verschoben.",
    prevention: [
      "Netzwerksegmentierung",
      "Privilegierte Accounts mit 2FA",
      "Regelmäßige Updates",
      "Offline-Backups",
      "Penetrationstests"
    ],
    insuranceCoverage: "Cyberversicherungen für Kliniken decken KIS-Ausfälle mit hohen Deckungssummen (5-20 Mio. €) ab.",
    relatedTerms: ["pacs", "ransomware"],
    resources: []
  },
  {
    slug: "pacs",
    name: "PACS (Picture Archiving Communication System)",
    category: "medizin",
    shortDef: "System zur Speicherung und Verwaltung medizinischer Bilder (Röntgen, CT, MRT).",
    longDescription: "PACS speichert, archiviert und verteilt medizinische Bilddaten. Nutzt DICOM-Standard zur Bildübertragung und -speicherung.",
    howItWorks: "Modalitäten senden DICOM-Bilder an PACS-Server. PACS speichert Bilder und verknüpft sie mit Patientendaten aus KIS.",
    exampleMedical: "Radiologie-Praxis erleidet Ransomware-Angriff. PACS-Server verschlüsselt, 15 Jahre Bildarchiv unzugänglich.",
    prevention: [
      "PACS-Software aktualisieren",
      "DICOM-Verschlüsselung aktivieren",
      "PACS nie öffentlich erreichbar",
      "Netzwerksegmentierung",
      "Verschlüsselte Backups"
    ],
    insuranceCoverage: "PACS-Ausfälle werden mit hohen Deckungssummen (2-10 Mio. €) versichert.",
    relatedTerms: ["kis", "ransomware"],
    resources: []
  },
  {
    slug: "pvs",
    name: "PVS (Praxisverwaltungssystem)",
    category: "medizin",
    shortDef: "Zentrale Software in Arztpraxen zur Verwaltung von Patientendaten, Terminplanung, Abrechnung.",
    longDescription: "PVS sind die meistgenutzte Software in Arztpraxen. Funktionen: Patientenstammdaten, Terminkalender, ePA-Anbindung, Abrechnung, E-Rezept.",
    howItWorks: "PVS läuft meist auf Windows-Server in Praxis. Clients greifen per LAN zu. TI-Anbindung über Konnektor.",
    exampleMedical: "Hausarztpraxis erleidet Ransomware. PVS-Server verschlüsselt, 3.200 Patientenakten unzugänglich. 5 Tage Praxis-Schließung.",
    prevention: [
      "PVS-Software aktuell halten",
      "Tägliche verschlüsselte Backups",
      "PVS isoliert vom Internet",
      "Zwei-Faktor-Authentifizierung",
      "Netzwerksegmentierung"
    ],
    insuranceCoverage: "PVS-Ausfälle werden umfassend versichert: Betriebsunterbrechung, Datenwiederherstellung, Forensik (1-3 Mio. € Standard).",
    relatedTerms: ["epa", "telematikinfrastruktur"],
    resources: []
  },
  {
    slug: "patientendaten",
    name: "Patientendaten",
    category: "medizin",
    shortDef: "Hochsensible Gesundheitsinformationen, die unter DSGVO besonderen Schutz genießen.",
    longDescription: "Patientendaten umfassen Stammdaten, Gesundheitsdaten, Behandlungsdaten und Abrechnungsdaten. DSGVO Art. 9: 'Besondere Kategorien' - Verarbeitung nur unter strengen Bedingungen.",
    howItWorks: "Patientendaten werden in PVS, KIS, PACS, ePA gespeichert. Zugriff nur für autorisierte Personen. Bei Datenpanne: Meldung binnen 72h.",
    exampleMedical: "Arztpraxis erleidet Phishing-Angriff. 1.200 Patientendaten werden exfiltriert und im Darknet verkauft. Bußgeld: 150.000€.",
    prevention: [
      "Verschlüsselung aller Patientendaten",
      "Rollenbasierte Berechtigungen",
      "Zwei-Faktor-Authentifizierung",
      "Zugriffsprotokolle aktivieren",
      "Mitarbeiter-Schulungen"
    ],
    insuranceCoverage: "Patientendaten-Lecks werden umfassend versichert: DSGVO-Bußgelder, Benachrichtigungskosten, Krisen-PR (2-10 Mio. €).",
    relatedTerms: ["dsgvo", "verschluesselung"],
    resources: []
  },
  {
    slug: "incident-response",
    name: "Incident Response",
    category: "incident",
    shortDef: "Strukturierter Prozess zur Bewältigung von Sicherheitsvorfällen.",
    longDescription: "Incident Response umfasst alle Maßnahmen zur Erkennung, Eindämmung, Beseitigung und Nachbereitung von Cyberangriffen.",
    howItWorks: "Ein definierter Ablaufplan regelt, wer bei einem Sicherheitsvorfall was zu tun hat - von der Erkennung bis zur Wiederherstellung.",
    exampleMedical: "Nach Ransomware-Angriff wird nach festgelegtem Plan vorgegangen: Systeme isolieren, Schaden bewerten, Wiederherstellung einleiten.",
    prevention: [
      "Incident-Response-Plan erstellen",
      "Regelmäßige Übungen durchführen",
      "Notfallkontakte pflegen",
      "Forensik-Tools bereithalten"
    ],
    insuranceCoverage: "Cyberversicherungen übernehmen oft Kosten für professionelle Incident-Response-Teams.",
    relatedTerms: ["notfallplan", "wiederherstellung"],
    resources: []
  },
  {
    slug: "forensik",
    name: "IT-Forensik",
    category: "incident",
    shortDef: "Untersuchung von IT-Systemen nach Sicherheitsvorfällen zur Beweissicherung.",
    longDescription: "IT-Forensik analysiert nach Cyberangriffen die Systeme, um Angriffswege zu rekonstruieren, Beweise zu sichern und Schwachstellen zu identifizieren.",
    howItWorks: "Forensiker untersuchen Logs, Speicherabbilder und Netzwerkdaten, um den Angriff nachzuvollziehen.",
    exampleMedical: "Nach Datenabfluss wird forensisch untersucht, welche Patientendaten kompromittiert wurden und wie der Angriff erfolgte.",
    prevention: [
      "Logging aktivieren und archivieren",
      "Forensik-Readiness vorbereiten",
      "Incident-Response-Plan mit Forensik-Phase",
      "Externe Forensik-Experten vorbereiten"
    ],
    insuranceCoverage: "Kosten für IT-Forensik sind in Cyberversicherungen meist eingeschlossen.",
    relatedTerms: ["incident-response", "logging"],
    resources: []
  },
  {
    slug: "wiederherstellung",
    name: "Disaster Recovery",
    category: "incident",
    shortDef: "Plan und Maßnahmen zur Wiederherstellung von IT-Systemen nach Ausfällen.",
    longDescription: "Disaster Recovery umfasst alle Prozesse und Technologien, um nach einem schwerwiegenden Vorfall den IT-Betrieb schnellstmöglich wiederherzustellen.",
    howItWorks: "Backups, redundante Systeme und dokumentierte Wiederherstellungsprozesse ermöglichen die zügige Wiederaufnahme des Praxisbetriebs.",
    exampleMedical: "Nach Ransomware-Angriff werden Systeme aus Backups wiederhergestellt und Praxisbetrieb innerhalb von 24h wieder aufgenommen.",
    prevention: [
      "Regelmäßige Backups nach 3-2-1-Regel",
      "Wiederherstellungsprozesse dokumentieren",
      "Recovery-Tests durchführen",
      "RTO und RPO definieren"
    ],
    insuranceCoverage: "Cyberversicherungen decken Kosten für Datenwiederherstellung und Systemrekonstruktion.",
    relatedTerms: ["backup", "rto-rpo"],
    resources: []
  },
  {
    slug: "business-continuity",
    name: "Business Continuity",
    category: "incident",
    shortDef: "Planung zur Aufrechterhaltung des Geschäftsbetriebs auch bei Störfällen.",
    longDescription: "Business Continuity Management stellt sicher, dass kritische Geschäftsprozesse auch bei Cyberangriffen, Ausfällen oder Naturkatastrophen weiterlaufen.",
    howItWorks: "Durch Notfallpläne, Ausweichsysteme und klare Verantwortlichkeiten wird die Handlungsfähigkeit auch in Krisensituationen gewährleistet.",
    exampleMedical: "Bei IT-Ausfall können Notfalltermine weiter wahrgenommen werden durch Papier-Backup und mobile Ersatzsysteme.",
    prevention: [
      "Business Impact Analyse durchführen",
      "Kritische Prozesse identifizieren",
      "Notfallpläne erstellen und testen",
      "Redundante Systeme vorhalten"
    ],
    insuranceCoverage: "Betriebsunterbrechungsschäden werden von Cyberversicherungen übernommen.",
    relatedTerms: ["wiederherstellung", "notfallplan"],
    resources: []
  },
  {
    slug: "penetration-test",
    name: "Penetrationstest",
    category: "sicherheit",
    shortDef: "Simulierter Cyberangriff zur Identifikation von Sicherheitslücken.",
    longDescription: "Bei einem Penetrationstest versuchen Sicherheitsexperten gezielt, in Systeme einzudringen, um Schwachstellen aufzudecken.",
    howItWorks: "Ethische Hacker nutzen die gleichen Methoden wie Kriminelle, dokumentieren gefundene Schwachstellen und geben Empfehlungen.",
    exampleMedical: "Penetrationstester deckt auf, dass Praxissoftware über Standard-Passwörter von außen erreichbar ist.",
    prevention: [
      "Regelmäßige Penetrationstests durchführen",
      "Gefundene Schwachstellen zeitnah beheben",
      "Re-Tests nach Maßnahmen",
      "Verschiedene Test-Szenarien"
    ],
    insuranceCoverage: "Manche Cyberversicherungen bieten Penetrationstests als Präventionsmaßnahme an.",
    relatedTerms: ["vulnerability", "security-audit"],
    resources: []
  },
  {
    slug: "security-audit",
    name: "Security Audit",
    category: "sicherheit",
    shortDef: "Systematische Überprüfung der IT-Sicherheitsmaßnahmen.",
    longDescription: "Ein Security Audit bewertet umfassend die IT-Sicherheit einer Organisation - von technischen Maßnahmen über Prozesse bis zu organisatorischen Regelungen.",
    howItWorks: "Auditoren prüfen anhand von Standards ob Sicherheitsmaßnahmen angemessen implementiert sind.",
    exampleMedical: "Externes Audit prüft, ob Praxis DSGVO-konforme Sicherheitsmaßnahmen für Patientendaten implementiert hat.",
    prevention: [
      "Jährliche Security Audits durchführen",
      "Empfehlungen zeitnah umsetzen",
      "Dokumentation pflegen",
      "Zertifizierungen anstreben"
    ],
    insuranceCoverage: "Regelmäßige Audits können Versicherungsprämien reduzieren.",
    relatedTerms: ["compliance", "iso-27001"],
    resources: []
  },
  {
    slug: "iso-27001",
    name: "ISO 27001",
    category: "compliance",
    shortDef: "Internationaler Standard für Informationssicherheits-Managementsysteme.",
    longDescription: "ISO 27001 definiert Anforderungen an ein systematisches Management der Informationssicherheit. Zertifizierung zeigt hohes Sicherheitsniveau.",
    howItWorks: "Der Standard gibt vor, wie Sicherheitsrisiken identifiziert, bewertet und durch geeignete Maßnahmen behandelt werden.",
    exampleMedical: "Große MVZ lassen ihr Informationssicherheits-Management nach ISO 27001 zertifizieren.",
    prevention: [
      "ISMS aufbauen und dokumentieren",
      "Risikomanagement etablieren",
      "Regelmäßige interne Audits",
      "Kontinuierliche Verbesserung"
    ],
    insuranceCoverage: "ISO 27001-Zertifizierung kann zu günstigeren Versicherungskonditionen führen.",
    relatedTerms: ["security-audit", "compliance"],
    resources: []
  },
  {
    slug: "bsi-grundschutz",
    name: "BSI-Grundschutz",
    category: "sicherheit",
    shortDef: "Methodik des BSI für angemessene IT-Sicherheit in Organisationen.",
    longDescription: "Das BSI bietet mit dem IT-Grundschutz eine bewährte Methodik und Maßnahmenkataloge für IT-Sicherheit.",
    howItWorks: "Der IT-Grundschutz-Kompendium enthält Bausteine mit konkreten Sicherheitsanforderungen für typische IT-Komponenten.",
    exampleMedical: "Praxis orientiert sich an BSI-Bausteinen für sichere Server, WLAN und Datensicherung.",
    prevention: [
      "Relevante Bausteine identifizieren",
      "Soll-Ist-Vergleich durchführen",
      "Maßnahmen priorisiert umsetzen",
      "Dokumentation pflegen"
    ],
    insuranceCoverage: "Umsetzung von BSI-Grundschutz zeigt Due Diligence und kann Versicherungsschutz verbessern.",
    relatedTerms: ["iso-27001", "security-audit"],
    resources: []
  },
  {
    slug: "patch-management",
    name: "Patch-Management",
    category: "sicherheit",
    shortDef: "Systematischer Prozess zur Verwaltung und Installation von Software-Updates.",
    longDescription: "Patch-Management stellt sicher, dass Sicherheitsupdates zeitnah und kontrolliert auf allen Systemen eingespielt werden.",
    howItWorks: "Updates werden getestet, priorisiert und zentral verteilt. Kritische Sicherheits-Patches werden bevorzugt behandelt.",
    exampleMedical: "Automatisches Patch-Management sorgt dafür, dass alle Praxis-PCs zeitnah Sicherheitsupdates erhalten.",
    prevention: [
      "Automatisierte Update-Verteilung",
      "Test-System für Updates",
      "Patch-Status überwachen",
      "Notfall-Patches priorisieren"
    ],
    insuranceCoverage: "Fehlendes Patch-Management kann Versicherungsschutz gefährden.",
    relatedTerms: ["vulnerability", "zero-day"],
    resources: []
  },
  {
    slug: "vulnerability",
    name: "Schwachstelle",
    category: "sicherheit",
    shortDef: "Sicherheitslücke in Software oder Systemen, die ausgenutzt werden kann.",
    longDescription: "Schwachstellen sind Fehler oder Lücken in Software, Hardware oder Prozessen, die von Angreifern ausgenutzt werden können.",
    howItWorks: "Vulnerabilities entstehen durch Programmierfehler, Designschwächen oder Fehlkonfigurationen und werden in CVE-Datenbanken erfasst.",
    exampleMedical: "Bekannte Schwachstelle in veralteter Praxissoftware-Version ermöglicht unbefugten Datenzugriff.",
    prevention: [
      "Vulnerability-Scanning durchführen",
      "Software aktuell halten",
      "CVE-Datenbanken überwachen",
      "Bug-Bounty-Programme nutzen"
    ],
    insuranceCoverage: "Schäden durch ausgenutzte Schwachstellen sind versichert, wenn angemessene Sorgfalt nachweisbar ist.",
    relatedTerms: ["patch-management", "zero-day"],
    resources: []
  },
  {
    slug: "endpoint-protection",
    name: "Endpoint Protection",
    category: "sicherheit",
    shortDef: "Umfassender Schutz von Endgeräten vor Cyberbedrohungen.",
    longDescription: "Endpoint Protection kombiniert Antivirus, Anti-Malware, Firewall und weitere Sicherheitsfunktionen zum ganzheitlichen Schutz von Arbeitsplätzen.",
    howItWorks: "Moderne EPP/EDR-Lösungen erkennen Bedrohungen durch Signaturen, Verhaltensanalyse und KI-gestützte Anomalieerkennung.",
    exampleMedical: "Endpoint-Protection-Lösung schützt alle Praxis-PCs vor Malware und blockiert verdächtige Aktivitäten.",
    prevention: [
      "Zentrale Endpoint-Protection-Lösung einsetzen",
      "Automatische Updates aktivieren",
      "Verhaltensbasierte Erkennung nutzen",
      "Regelmäßige Scans durchführen"
    ],
    insuranceCoverage: "Endpoint Protection wird von Versicherern oft vorausgesetzt oder reduziert Prämien.",
    relatedTerms: ["malware", "edr"],
    resources: []
  },
  {
    slug: "edr",
    name: "EDR",
    category: "sicherheit",
    shortDef: "Erweiterte Endpoint-Sicherheit mit Erkennung und Reaktion auf Bedrohungen.",
    longDescription: "EDR geht über klassischen Virenschutz hinaus und bietet kontinuierliche Überwachung, Bedrohungserkennung und automatisierte Reaktionsmöglichkeiten.",
    howItWorks: "EDR-Systeme sammeln kontinuierlich Daten von Endgeräten, analysieren Verhalten und können bei Anomalien automatisch Gegenmaßnahmen einleiten.",
    exampleMedical: "EDR erkennt ungewöhnliche Dateiaktivität auf Praxis-PC und isoliert automatisch das betroffene System.",
    prevention: [
      "EDR-Lösung implementieren",
      "SOC oder MSSP anbinden",
      "Incident-Response-Prozesse definieren",
      "Regelmäßige Threat-Hunting-Aktivitäten"
    ],
    insuranceCoverage: "EDR-Einsatz kann zu besseren Versicherungskonditionen führen.",
    relatedTerms: ["endpoint-protection", "incident-response"],
    resources: []
  },
  {
    slug: "siem",
    name: "SIEM",
    category: "sicherheit",
    shortDef: "Zentrale Sammlung und Analyse von Sicherheitsereignissen.",
    longDescription: "SIEM-Systeme aggregieren Logs und Events aus verschiedenen Quellen, korrelieren diese und erkennen so Sicherheitsvorfälle.",
    howItWorks: "Logs von Firewalls, Servern, Endpoints werden zentral gesammelt, in Echtzeit analysiert und bei Auffälligkeiten Alarme ausgelöst.",
    exampleMedical: "SIEM korreliert fehlerhafte Login-Versuche und Netzwerk-Anomalien zu Angriffsversuch auf Praxis-Netzwerk.",
    prevention: [
      "SIEM-System implementieren",
      "Use Cases definieren",
      "Alerting konfigurieren",
      "Regelmäßige Auswertung"
    ],
    insuranceCoverage: "SIEM-Einsatz zeigt professionelle Sicherheitsorganisation und verbessert Versicherungskonditionen.",
    relatedTerms: ["logging", "incident-response"],
    resources: []
  },
  {
    slug: "logging",
    name: "Security Logging",
    category: "sicherheit",
    shortDef: "Protokollierung sicherheitsrelevanter Ereignisse zur Nachvollziehbarkeit.",
    longDescription: "Security Logging erfasst systematisch alle sicherheitsrelevanten Ereignisse wie Logins, Zugriffe auf Daten oder Systemänderungen.",
    howItWorks: "Logs werden zentral gesammelt, gegen Manipulation geschützt und für Audit- und Forensik-Zwecke aufbewahrt.",
    exampleMedical: "Alle Zugriffe auf elektronische Patientenakten werden geloggt und können bei Bedarf ausgewertet werden.",
    prevention: [
      "Umfassendes Logging aktivieren",
      "Logs zentral und unveränderbar speichern",
      "Aufbewahrungsfristen einhalten",
      "Regelmäßige Auswertung"
    ],
    insuranceCoverage: "Logging ist oft Compliance-Anforderung und für Schadensbeweis wichtig.",
    relatedTerms: ["siem", "forensik"],
    resources: []
  },
  {
    slug: "netzwerksegmentierung",
    name: "Netzwerksegmentierung",
    category: "sicherheit",
    shortDef: "Aufteilung des Netzwerks in isolierte Bereiche zur Schadensbegrenzung.",
    longDescription: "Durch Segmentierung wird das Netzwerk in verschiedene Zonen aufgeteilt, die durch Firewalls voneinander getrennt sind.",
    howItWorks: "Kritische Systeme werden in eigene Netzsegmente platziert, Zugriffe erfolgen nur über kontrollierte Übergänge.",
    exampleMedical: "Praxissoftware läuft in separatem Netzwerk, Gäste-WLAN ist komplett isoliert.",
    prevention: [
      "Netzwerk in sinnvolle Segmente aufteilen",
      "Firewalls zwischen Segmenten",
      "Least-Privilege-Prinzip anwenden",
      "Micro-Segmentation wo sinnvoll"
    ],
    insuranceCoverage: "Segmentierung begrenzt Schadenausmaß bei Angriffen und wird von Versicherern positiv bewertet.",
    relatedTerms: ["firewall", "zero-trust"],
    resources: []
  },
  {
    slug: "zero-trust",
    name: "Zero Trust",
    category: "sicherheit",
    shortDef: "Sicherheitskonzept, bei dem grundsätzlich niemandem vertraut wird.",
    longDescription: "Zero Trust geht davon aus, dass Bedrohungen sowohl außerhalb als auch innerhalb des Netzwerks existieren. Jeder Zugriff muss authentifiziert und autorisiert werden.",
    howItWorks: "Statt eines klassischen Perimeter-Schutzes wird jeder Zugriff auf Ressourcen überprüft - unabhängig davon, von wo er kommt.",
    exampleMedical: "Auch interne Mitarbeiter müssen sich bei jedem Zugriff auf Patientendaten authentifizieren.",
    prevention: [
      "Mikro-Segmentierung implementieren",
      "Starke Authentifizierung überall",
      "Least-Privilege-Prinzip konsequent",
      "Kontinuierliche Überprüfung"
    ],
    insuranceCoverage: "Zero-Trust-Architektur reduziert Risiken erheblich und verbessert Versicherungsbedingungen.",
    relatedTerms: ["netzwerksegmentierung", "2fa"],
    resources: []
  },
  {
    slug: "identity-management",
    name: "Identity & Access Management",
    category: "sicherheit",
    shortDef: "Verwaltung von Benutzeridentitäten und Zugriffsrechten.",
    longDescription: "IAM regelt, wer Zugriff auf welche Systeme und Daten hat. Es umfasst Benutzerverwaltung, Rechtevergabe und Authentifizierung.",
    howItWorks: "Zentrale Verwaltung von Benutzerkonten, Rollen und Rechten. Single Sign-On ermöglicht Zugriff auf mehrere Systeme mit einer Anmeldung.",
    exampleMedical: "Ärzte, MFA und Verwaltung haben unterschiedliche Zugriffsrechte auf Patientendaten je nach Rolle.",
    prevention: [
      "Zentrale Benutzerverwaltung",
      "Rollenbasierte Rechtevergabe",
      "Regelmäßige Zugriffs-Reviews",
      "Automatisiertes Offboarding"
    ],
    insuranceCoverage: "Sauberes IAM verhindert unbefugte Zugriffe und ist DSGVO-Anforderung.",
    relatedTerms: ["2fa", "sso"],
    resources: []
  },
  {
    slug: "sso",
    name: "Single Sign-On",
    category: "sicherheit",
    shortDef: "Einmalige Anmeldung für Zugriff auf mehrere Systeme.",
    longDescription: "Mit SSO melden sich Benutzer nur einmal an und erhalten Zugriff auf alle berechtigten Systeme, ohne sich mehrfach authentifizieren zu müssen.",
    howItWorks: "Ein zentraler Identity Provider verwaltet die Authentifizierung. Nach erfolgreicher Anmeldung werden Tokens für Zugriff auf verbundene Systeme ausgestellt.",
    exampleMedical: "Arzt meldet sich einmal an und kann dann auf Praxissoftware, E-Mail und Befundsystem zugreifen.",
    prevention: [
      "SSO-Lösung implementieren",
      "Starke Authentifizierung am SSO",
      "MFA für SSO-Login",
      "Session-Timeouts konfigurieren"
    ],
    insuranceCoverage: "SSO verbessert Security und Usability, reduziert Passwort-Risiken.",
    relatedTerms: ["identity-management", "2fa"],
    resources: []
  },
  {
    slug: "rto-rpo",
    name: "RTO & RPO",
    category: "incident",
    shortDef: "Recovery Time Objective und Recovery Point Objective - Kennzahlen für Wiederherstellungsziele.",
    longDescription: "RTO definiert die maximal tolerierbare Ausfallzeit, RPO den maximal akzeptablen Datenverlust bei einem Störfall.",
    howItWorks: "RTO: Wie schnell muss System wieder laufen? RPO: Wie viel Datenverlust ist akzeptabel? Diese Werte bestimmen Backup- und DR-Strategie.",
    exampleMedical: "Praxis definiert: Praxissoftware muss innerhalb 4h wieder laufen, Datenverlust max. 1h.",
    prevention: [
      "RTO/RPO für kritische Systeme definieren",
      "Backup-Strategie danach ausrichten",
      "Regelmäßige Recovery-Tests",
      "Dokumentation der Anforderungen"
    ],
    insuranceCoverage: "RTO/RPO-Werte bestimmen benötigte Versicherungssummen für Betriebsunterbrechung.",
    relatedTerms: ["backup", "wiederherstellung"],
    resources: []
  },
  {
    slug: "notfallplan",
    name: "IT-Notfallplan",
    category: "incident",
    shortDef: "Dokumentierter Plan für IT-Notfälle mit Verantwortlichkeiten und Maßnahmen.",
    longDescription: "Der IT-Notfallplan beschreibt, wer bei verschiedenen IT-Notfällen was zu tun hat - von Kontaktdaten über Ablaufpläne bis zu Wiederherstellungsschritten.",
    howItWorks: "Für verschiedene Szenarien sind Rollen, Verantwortlichkeiten und Handlungsschritte definiert.",
    exampleMedical: "Bei Ransomware-Angriff ist klar geregelt: Wer isoliert Systeme? Wer informiert Behörden? Wer kommuniziert mit Patienten?",
    prevention: [
      "Notfallplan erstellen und dokumentieren",
      "Verantwortlichkeiten definieren",
      "Kontaktdaten aktuell halten",
      "Plan regelmäßig testen und üben"
    ],
    insuranceCoverage: "Vorhandener und getesteter Notfallplan wird von Versicherern oft vorausgesetzt.",
    relatedTerms: ["incident-response", "business-continuity"],
    resources: []
  },
  {
    slug: "awareness-training",
    name: "Security Awareness Training",
    category: "sicherheit",
    shortDef: "Schulungen zur Sensibilisierung von Mitarbeitern für IT-Sicherheit.",
    longDescription: "Awareness-Trainings schulen Mitarbeiter im sicheren Umgang mit IT-Systemen und sensibilisieren für Cyberbedrohungen.",
    howItWorks: "Regelmäßige Schulungen, Phishing-Simulationen und Security-Tipps halten das Sicherheitsbewusstsein hoch.",
    exampleMedical: "Praxisteam wird geschult, Phishing-Mails zu erkennen und sichere Passwörter zu verwenden.",
    prevention: [
      "Regelmäßige Schulungen (mind. jährlich)",
      "Phishing-Simulationen durchführen",
      "Security-Awareness-Kampagnen",
      "Neue Mitarbeiter sofort schulen"
    ],
    insuranceCoverage: "Nachweisbare Schulungen werden von Versicherern positiv bewertet und können Prämien reduzieren.",
    relatedTerms: ["phishing", "social-engineering"],
    resources: []
  },
  {
    slug: "mfa",
    name: "Multi-Faktor-Authentifizierung",
    category: "sicherheit",
    shortDef: "Anmeldung mit mehreren unabhängigen Faktoren für höhere Sicherheit.",
    longDescription: "MFA kombiniert mindestens zwei verschiedene Faktoren: Wissen (Passwort), Besitz (Token) oder biometrisches Merkmal.",
    howItWorks: "Neben dem Passwort wird ein zweiter Faktor wie SMS-Code, Authenticator-App oder Hardware-Token benötigt.",
    exampleMedical: "Zugriff auf Praxissoftware erfordert Passwort UND Code aus Authenticator-App.",
    prevention: [
      "MFA für alle kritischen Systeme aktivieren",
      "Authenticator-Apps bevorzugen",
      "Backup-Codes sicher verwahren",
      "Hardware-Token für Admin-Zugriffe"
    ],
    insuranceCoverage: "MFA wird von vielen Versicherern mittlerweile vorausgesetzt oder stark empfohlen.",
    relatedTerms: ["2fa", "identity-management"],
    resources: []
  },
  {
    slug: "sql-injection",
    name: "SQL-Injection",
    category: "bedrohungen",
    shortDef: "Angriffstechnik, bei der schädlicher Code in Datenbank-Abfragen eingeschleust wird.",
    longDescription: "Bei SQL-Injection wird bösartiger SQL-Code in Eingabefelder eingeschleust, um Zugriff auf Datenbanken zu erlangen oder Daten zu manipulieren.",
    howItWorks: "Angreifer nutzen unzureichend validierte Eingabefelder, um SQL-Befehle in die Datenbank einzuschleusen.",
    exampleMedical: "Über manipulierte Eingabe im Patientenportal erlangt Angreifer Zugriff auf gesamte Patientendatenbank.",
    prevention: [
      "Prepared Statements verwenden",
      "Input-Validierung implementieren",
      "Least-Privilege-Prinzip bei Datenbankzugriffen",
      "Web Application Firewall"
    ],
    insuranceCoverage: "Datenschutzverletzungen durch SQL-Injection sind versichert, inklusive Wiederherstellungskosten.",
    relatedTerms: ["datenschutz", "patientendaten"],
    resources: []
  },
  {
    slug: "man-in-the-middle",
    name: "Man-in-the-Middle",
    category: "bedrohungen",
    shortDef: "Angriff, bei dem Kommunikation zwischen zwei Parteien heimlich abgefangen wird.",
    longDescription: "Bei einem MITM-Angriff positioniert sich der Angreifer zwischen zwei kommunizierenden Parteien und kann Daten mitlesen oder manipulieren.",
    howItWorks: "Der Angreifer fängt die Kommunikation ab, indem er sich als vertrauenswürdiger Vermittler ausgibt.",
    exampleMedical: "Angreifer im WLAN fängt unverschlüsselte Übertragung von Patientendaten ab.",
    prevention: [
      "End-to-End-Verschlüsselung verwenden",
      "VPN für sensible Verbindungen",
      "Zertifikate prüfen",
      "Sichere WLAN-Konfiguration"
    ],
    insuranceCoverage: "Schäden durch MITM-Angriffe sind versichert, wenn Patientendaten kompromittiert werden.",
    relatedTerms: ["verschluesselung", "vpn"],
    resources: []
  },
  {
    slug: "keylogger",
    name: "Keylogger",
    category: "bedrohungen",
    shortDef: "Software oder Hardware, die alle Tastatureingaben aufzeichnet.",
    longDescription: "Keylogger protokollieren jede Tastatureingabe und können so Passwörter, Patientendaten und andere sensible Informationen erfassen.",
    howItWorks: "Software-Keylogger laufen im Hintergrund und zeichnen alle Eingaben auf. Hardware-Keylogger werden zwischen Tastatur und PC geschaltet.",
    exampleMedical: "Keylogger erfasst Login-Daten für Praxissoftware und Patientendaten bei der Eingabe.",
    prevention: [
      "Anti-Malware mit Keylogger-Erkennung",
      "Virtuelle Tastaturen für sensible Eingaben",
      "Physische Sicherheit der Hardware",
      "Multi-Faktor-Authentifizierung"
    ],
    insuranceCoverage: "Schäden durch Keylogger-Angriffe sind versichert, wenn Zugangsdaten kompromittiert werden.",
    relatedTerms: ["spyware", "malware", "2fa"],
    resources: []
  },
  {
    slug: "web-application-firewall",
    name: "Web Application Firewall",
    category: "sicherheit",
    shortDef: "Firewall zum Schutz von Webanwendungen vor Angriffen.",
    longDescription: "Eine WAF filtert und überwacht HTTP-Traffic zu Webanwendungen und blockiert Angriffe wie SQL-Injection oder Cross-Site-Scripting.",
    howItWorks: "Die WAF analysiert HTTP-Requests nach bekannten Angriffsmustern und blockiert bösartigen Traffic, bevor er die Anwendung erreicht.",
    exampleMedical: "WAF schützt Patienten-Portal vor Angriffen wie SQL-Injection oder Bot-basierten Attacken.",
    prevention: [
      "WAF vor Webanwendungen platzieren",
      "Regelmäßig Regelwerk aktualisieren",
      "Logs auswerten",
      "False-Positives minimieren"
    ],
    insuranceCoverage: "WAF-Einsatz für öffentlich erreichbare Systeme wird empfohlen.",
    relatedTerms: ["firewall", "sql-injection"],
    resources: []
  },
  {
    slug: "e-rezept",
    name: "E-Rezept",
    category: "medizin",
    shortDef: "Elektronisches Rezept, das über die Telematikinfrastruktur ausgestellt und eingelöst wird.",
    longDescription: "Das E-Rezept ersetzt das klassische Papierrezept. Patienten erhalten einen Code, den sie in der Apotheke vorlegen.",
    howItWorks: "Arzt erstellt E-Rezept in Praxissoftware, lädt es verschlüsselt auf E-Rezept-Server. Patient erhält QR-Code per App oder Ausdruck.",
    exampleMedical: "Arzt verschreibt Medikament per E-Rezept, Patient löst es in beliebiger Apotheke mit QR-Code ein.",
    prevention: [
      "Sichere TI-Anbindung",
      "Verschlüsselte Datenübertragung",
      "Zugriffskontrolle",
      "Datenschutzkonforme Archivierung"
    ],
    insuranceCoverage: "E-Rezept-bezogene Datenschutzvorfälle sind mitversichert.",
    relatedTerms: ["telematikinfrastruktur", "epa"],
    resources: []
  },
  {
    slug: "cloud-security",
    name: "Cloud-Sicherheit",
    category: "sicherheit",
    shortDef: "Sicherheitsmaßnahmen zum Schutz von in Cloud-Umgebungen gespeicherten Daten.",
    longDescription: "Cloud Security umfasst technische und organisatorische Maßnahmen zum Schutz von Cloud-Diensten und -Daten.",
    howItWorks: "Verschlüsselung, Zugriffskontrollen, Monitoring und Compliance-Maßnahmen schützen Cloud-Ressourcen.",
    exampleMedical: "Praxis nutzt Cloud-Backup-Lösung mit Ende-zu-Ende-Verschlüsselung für Patientendaten.",
    prevention: [
      "Cloud-Provider sorgfältig auswählen",
      "Ende-zu-Ende-Verschlüsselung",
      "Auftragsverarbeitungsvertrag abschließen",
      "Zugriffe protokollieren"
    ],
    insuranceCoverage: "Cloud-Datenverluste sind versichert, wenn angemessene Sicherheitsmaßnahmen vorhanden waren.",
    relatedTerms: ["verschluesselung", "dsgvo"],
    resources: []
  },
  {
    slug: "container-security",
    name: "Container-Sicherheit",
    category: "sicherheit",
    shortDef: "Schutzmaßnahmen für containerisierte Anwendungen.",
    longDescription: "Container-Security schützt containerisierte Workloads durch Image-Scanning, Runtime-Protection und Netzwerk-Policies.",
    howItWorks: "Container werden auf Schwachstellen gescannt, zur Laufzeit überwacht und durch Netzwerk-Policies isoliert.",
    exampleMedical: "Krankenhaus betreibt KIS in Container-Umgebung mit automatischem Security-Scanning.",
    prevention: [
      "Image-Scanning implementieren",
      "Least-Privilege-Container",
      "Runtime-Security nutzen",
      "Sichere Registry verwenden"
    ],
    insuranceCoverage: "Container-Schwachstellen können zu Datenlecks führen - Versicherungsschutz greift bei nachweisbarer Sorgfalt.",
    relatedTerms: ["vulnerability", "cloud-security"],
    resources: []
  },
  {
    slug: "api-security",
    name: "API-Sicherheit",
    category: "sicherheit",
    shortDef: "Schutz von Programmierschnittstellen vor unbefugtem Zugriff und Missbrauch.",
    longDescription: "API Security schützt Schnittstellen durch Authentifizierung, Verschlüsselung, Rate-Limiting und Input-Validierung.",
    howItWorks: "APIs werden durch OAuth, API-Keys, Verschlüsselung und WAFs geschützt.",
    exampleMedical: "Schnittstelle zwischen Praxissoftware und Labor wird durch API-Key und TLS abgesichert.",
    prevention: [
      "OAuth 2.0 / OpenID Connect",
      "API-Gateway einsetzen",
      "Rate-Limiting konfigurieren",
      "Input-Validierung"
    ],
    insuranceCoverage: "API-Schwachstellen können zu massiven Datenlecks führen - im Versicherungsschutz enthalten.",
    relatedTerms: ["verschluesselung", "identity-management"],
    resources: []
  },
  {
    slug: "supply-chain-attack",
    name: "Supply-Chain-Angriff",
    category: "bedrohungen",
    shortDef: "Angriff über kompromittierte Software-Lieferketten oder Dienstleister.",
    longDescription: "Supply-Chain-Angriffe zielen auf Schwachstellen bei Software-Anbietern oder Dienstleistern, um indirekt deren Kunden anzugreifen.",
    howItWorks: "Angreifer kompromittieren Software-Updates oder Dienstleister-Systeme und verbreiten so Schadsoftware.",
    exampleMedical: "Angriff auf Praxissoftware-Hersteller führt zu kompromittierten Updates für hunderte Arztpraxen.",
    prevention: [
      "Software-Quellen verifizieren",
      "Digitale Signaturen prüfen",
      "Dienstleister sorgfältig auswählen",
      "Verträge mit Security-SLAs"
    ],
    insuranceCoverage: "Supply-Chain-Angriffe sind mitversichert, auch wenn primär Dritte betroffen sind.",
    relatedTerms: ["malware", "ransomware"],
    resources: []
  },
  {
    slug: "insider-threat",
    name: "Insider-Bedrohung",
    category: "bedrohungen",
    shortDef: "Sicherheitsrisiko durch eigene Mitarbeiter - absichtlich oder fahrlässig.",
    longDescription: "Insider-Bedrohungen entstehen durch Mitarbeiter, die absichtlich oder unabsichtlich Sicherheitsrichtlinien verletzen oder Daten kompromittieren.",
    howItWorks: "Mitarbeiter missbrauchen ihre Zugriffsrechte zum Datendiebstahl oder handeln fahrlässig bei der Datensicherheit.",
    exampleMedical: "MFA verkauft Patientendaten aus Rache nach Kündigung. Oder: Arzt verliert USB-Stick mit unverschlüsselten Patientendaten.",
    prevention: [
      "Least-Privilege-Prinzip konsequent",
      "Zugriffsrechte regelmäßig reviewen",
      "Offboarding-Prozess definieren",
      "User-Activity-Monitoring"
    ],
    insuranceCoverage: "Insider-Vorfälle sind versichert, auch bei grober Fahrlässigkeit einzelner Mitarbeiter.",
    relatedTerms: ["identity-management", "logging"],
    resources: []
  },
  {
    slug: "data-breach",
    name: "Datenpanne",
    category: "incident",
    shortDef: "Sicherheitsvorfall mit unbefugtem Zugriff auf personenbezogene Daten.",
    longDescription: "Eine Datenpanne liegt vor, wenn personenbezogene Daten unrechtmäßig offengelegt, verändert, gelöscht oder unzugänglich gemacht wurden.",
    howItWorks: "Bei Datenpanne besteht DSGVO-Meldepflicht binnen 72h an Aufsichtsbehörde. Bei hohem Risiko auch Benachrichtigung Betroffener.",
    exampleMedical: "Nach Ransomware-Angriff wurden 5.000 Patientendaten kompromittiert. Praxis muss binnen 72h melden.",
    prevention: [
      "Datenschutz-Folgenabschätzung",
      "Verschlüsselung sensibler Daten",
      "Zugriffskontrolle",
      "Incident-Response-Plan"
    ],
    insuranceCoverage: "Datenpannen-Management ist Kernleistung: Meldung, Betroffenen-Info, Bußgelder, Schadensersatz.",
    relatedTerms: ["dsgvo", "patientendaten"],
    resources: []
  },
  {
    slug: "compliance",
    name: "Compliance",
    category: "compliance",
    shortDef: "Einhaltung gesetzlicher und regulatorischer Vorgaben.",
    longDescription: "Compliance bedeutet die Erfüllung aller relevanten Gesetze, Vorschriften und Standards - insbesondere DSGVO, BDSG und berufsrechtliche Vorgaben.",
    howItWorks: "Durch Richtlinien, Prozesse, Kontrollen und Dokumentation wird sichergestellt, dass alle Anforderungen erfüllt werden.",
    exampleMedical: "Arztpraxis implementiert DSGVO-Compliance: Verarbeitungsverzeichnis, TOMs, AVV, Datenschutz-Folgenabschätzung.",
    prevention: [
      "Compliance-Management-System aufbauen",
      "Regelmäßige Audits",
      "Mitarbeiter schulen",
      "Dokumentation pflegen"
    ],
    insuranceCoverage: "Compliance-Verstöße können zu Bußgeldern führen - diese sind mitversichert.",
    relatedTerms: ["dsgvo", "bdsg"],
    resources: []
  },
  {
    slug: "datenschutz-folgenabschaetzung",
    name: "Datenschutz-Folgenabschätzung",
    category: "compliance",
    shortDef: "Risikobewertung bei Verarbeitung besonders sensibler Daten.",
    longDescription: "Eine DSFA ist erforderlich, wenn Datenverarbeitung voraussichtlich ein hohes Risiko für Betroffene darstellt - insbesondere bei Gesundheitsdaten.",
    howItWorks: "Systematische Bewertung der Verarbeitung: Welche Risiken? Welche Maßnahmen? Ist Risiko akzeptabel?",
    exampleMedical: "Klinik führt DSFA durch vor Einführung KI-gestützter Diagnose-Software mit Patientendaten.",
    prevention: [
      "DSFA bei neuen Verarbeitungen prüfen",
      "Risiken systematisch bewerten",
      "Maßnahmen dokumentieren",
      "DSB einbeziehen"
    ],
    insuranceCoverage: "Fehlende DSFA kann zu Bußgeldern führen - Compliance-Verstöße sind mitversichert.",
    relatedTerms: ["dsgvo", "patientendaten"],
    resources: []
  },
  {
    slug: "toms",
    name: "TOMs",
    category: "compliance",
    shortDef: "Technische und organisatorische Maßnahmen zum Datenschutz.",
    longDescription: "TOMs sind konkrete Sicherheitsmaßnahmen zum Schutz personenbezogener Daten, die DSGVO Art. 32 fordert.",
    howItWorks: "TOMs umfassen Zutrittskontrolle, Zugangskontrolle, Zugriffskontrolle, Weitergabekontrolle, Verschlüsselung etc.",
    exampleMedical: "Praxis dokumentiert TOMs: Alarmanlage, Passwörter, Rechtevergabe, verschlüsselte E-Mails, Firewall.",
    prevention: [
      "TOMs systematisch erfassen",
      "Dem Risiko angemessen",
      "Regelmäßig überprüfen",
      "Dokumentation aktuell halten"
    ],
    insuranceCoverage: "Fehlende oder unzureichende TOMs können Versicherungsschutz gefährden.",
    relatedTerms: ["dsgvo", "verschluesselung"],
    resources: []
  },
  {
    slug: "avv",
    name: "Auftragsverarbeitungsvertrag",
    category: "compliance",
    shortDef: "Vertrag zwischen Verantwortlichem und Auftragsverarbeiter gemäß DSGVO Art. 28.",
    longDescription: "Ein AVV regelt die Datenverarbeitung durch Dienstleister und ist DSGVO-Pflicht bei externer Datenverarbeitung.",
    howItWorks: "AVV definiert Zweck, Art der Verarbeitung, TOMs, Unterauftragsverhältnisse und Kontrollrechte.",
    exampleMedical: "Arztpraxis schließt AVV mit IT-Dienstleister, Cloud-Backup-Anbieter und Abrechnungsstelle.",
    prevention: [
      "AVV vor Auftragserteilung abschließen",
      "Muster-AVV verwenden",
      "Dienstleister prüfen",
      "Regelmäßige Kontrollen"
    ],
    insuranceCoverage: "Fehlende AVVs können zu Bußgeldern führen - Compliance-Verstöße mitversichert.",
    relatedTerms: ["dsgvo", "compliance"],
    resources: []
  },
  {
    slug: "datenschutzbeauftragter",
    name: "Datenschutzbeauftragter",
    category: "compliance",
    shortDef: "Person, die Datenschutz-Compliance überwacht - Pflicht ab 20 Mitarbeitern.",
    longDescription: "Der DSB überwacht Einhaltung der DSGVO, berät bei Datenschutzfragen und ist Ansprechpartner für Aufsichtsbehörde.",
    howItWorks: "DSB kann intern oder extern bestellt werden. Er unterrichtet Organisation, berät und überwacht Compliance.",
    exampleMedical: "Gemeinschaftspraxis mit 25 Mitarbeitern bestellt externen Datenschutzbeauftragten.",
    prevention: [
      "DSB rechtzeitig bestellen",
      "Fachkunde sicherstellen",
      "Kontaktdaten veröffentlichen",
      "DSB in Prozesse einbinden"
    ],
    insuranceCoverage: "Fehlender DSB trotz Pflicht führt zu Bußgeldern - diese sind mitversichert.",
    relatedTerms: ["dsgvo", "bdsg"],
    resources: []
  }
];
